/**
 * Tag string helpers for the v3 transaction tag system.
 *
 * Storage format: a single string column on `transactions` that is either
 * empty (`''` → no tags) or comma-joined chips, each starting with `#`,
 * e.g. `#網球培訓,#備孕計劃,#京都旅行`.
 *
 * Sharp edges:
 *   - We sanitise on write — no commas inside a tag (commas are the
 *     storage delimiter), no whitespace at the boundaries, deduplicated,
 *     and every non-empty chip is `#`-prefixed exactly once.
 *   - parse(stringify(arr)) === arr (after sanitisation): the round-trip
 *     is stable so optimistic UI never disagrees with what hits Sheets.
 *   - Empty / whitespace-only chips are dropped silently.
 *   - The `#` prefix is part of the stored value (the design wants it as
 *     a visible chip prefix). Callers MUST NOT strip it off and re-add.
 */

const TAG_DELIM = ',';
const TAG_PREFIX = '#';

/** Sanitise a single chip — strip whitespace + commas, ensure `#` prefix.
 *  Returns null if the resulting tag is empty (caller drops it). */
export function sanitizeTag(raw: string): string | null {
  if (typeof raw !== 'string') return null;
  // Drop the delimiter outright; users should not see it survive.
  let t = raw.replace(/,/g, '').trim();
  if (t.length === 0) return null;
  // Strip leading `#` characters then re-add exactly one. Idempotent for
  // both `'#foo'` and `'foo'`.
  while (t.startsWith(TAG_PREFIX)) t = t.slice(TAG_PREFIX.length);
  t = t.trim();
  if (t.length === 0) return null;
  return TAG_PREFIX + t;
}

/** Parse the stored string into an array of clean tags. */
export function parseTags(s: string | null | undefined): string[] {
  if (s == null || s === '') return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of s.split(TAG_DELIM)) {
    const tag = sanitizeTag(part);
    if (tag == null) continue;
    if (seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out;
}

/** Serialise an array of (already user-input) tags back into the storage
 *  string. Sanitises + dedupes so callers can pass raw input directly. */
export function stringifyTags(tags: ReadonlyArray<string>): string {
  if (!tags || tags.length === 0) return '';
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of tags) {
    const tag = sanitizeTag(raw);
    if (tag == null) continue;
    if (seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out.join(TAG_DELIM);
}

/**
 * Backward-compatible read helper. After v3 migration runs, transactions
 * have `tags` populated and `project` cleared. But during a partial upgrade
 * window (or for a row the migration hasn't touched yet) we still want to
 * surface the legacy `project` value as a single chip, without writing it
 * back. Use this in display/aggregation paths.
 */
export function readDisplayTags(tx: { tags?: string; project?: string }): string[] {
  const fromTags = parseTags(tx.tags);
  if (fromTags.length > 0) return fromTags;
  const project = (tx.project ?? '').trim();
  if (project === '') return [];
  const tag = sanitizeTag(project);
  return tag ? [tag] : [];
}
